import React, { useCallback, useState } from 'react';
import { Redirect } from 'react-router-dom';
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Box,
  Grid,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { COLOR_PRIMARY, RoutesMap } from 'src/const';
import Copyright from './Copyright';
import { Alert } from '@material-ui/lab';
import { LoginPropTypes } from 'src/interfaces/iproptypes';
import { IconButton, InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { makeLowerCase } from 'src/utils/functions';
import { GoogleAuth } from '../GoogleAuth';

import AirbticsLogoFull from 'src/App/assets/logoMobile.jpg';
import { useEmailValidation } from 'src/hooks/useCustomHooks';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
  },
  image: {
    backgroundImage: 'url(https://airbtics-resource.s3.us-east-2.amazonaws.com/Creativity.png)',
    backgroundRepeat: 'no-repeat',
    backgroundColor:
      theme.palette.type === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  paper: {
    margin: theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: COLOR_PRIMARY,
    '&:hover': {
      backgroundColor: COLOR_PRIMARY,
    },
  },
  progressBar: {
    width: '100%',
  },
  logo: {
    width: '150px',
    marginBottom: '20px',
  },
  signinText: {
    marginTop: '20px',
    marginBottom: '0px',
    fontSize: '20px',
    fontWeight: 'bolder',
    color: '#7c7c7c',
  },
}));

const Login = ({
  loginThunk,
  getTokenThunk,
  loading,
  error,
  redirectToSignupPage,
  landing,
  device,
}: LoginPropTypes) => {
  const { email, emailError, emailErrorMessage, handleEmailChange } = useEmailValidation();

  const [password, changePassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const classes = useStyles();

  const handlePasswordChange = useCallback(event => {
    changePassword(event.target.value);
  }, []);

  const handleLogin = useCallback(
    event => {
      event.preventDefault();

      loginThunk({ email, password });
    },
    [loginThunk, email, password]
  );
  const isLoggedIn = Boolean(getTokenThunk());

  const urlToRedirect = window.location.pathname;

  if (isLoggedIn) {
    window.localStorage.setItem('user-email', email);
    if (
      urlToRedirect.includes(
        '/airbnb-data/united-kingdom/0/the%20city%20of%20brighton%20and%20hove'
      )
    ) {
      window.location.href = '/airbnb-data';
    } else {
      window.location.href = urlToRedirect;
    }
  }

  return (
    <>
      {loading && <LinearProgress className={classes.progressBar} />}

      <CssBaseline />
      <div className={classes.paper}>
        <img src={AirbticsLogoFull} alt="Airbtics Logo" className={classes.logo} />
        {error && <Alert severity="error">Password incorrect or no email is found!</Alert>}

        <GoogleAuth landing={landing} device={device} urlToRedirect={urlToRedirect} />

        <Typography component="h1" variant="h5" className={classes.signinText}>
          Sign in
        </Typography>

        <form className={classes.form} noValidate onSubmit={handleLogin}>
          <TextField
            onChange={handleEmailChange}
            value={email}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            error={emailError}
            helperText={emailError ? emailErrorMessage : ''}
          />
          <TextField
            onChange={handlePasswordChange}
            value={password}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(prev => !prev)}
                  >
                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={loading}
          >
            Sign In
          </Button>
          <Grid container>
            <Grid item xs>
              <Link href="/reset-password" variant="body2">
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              <Link onClick={redirectToSignupPage} variant="body2" style={{ cursor: 'pointer' }}>
                Don't have an account? Sign Up
              </Link>
            </Grid>
          </Grid>
          <Box mt={5}>
            <Copyright />
          </Box>
        </form>
      </div>
    </>
  );
};
/*
const Login = ({ loginThunk, getTokenThunk, loading, error }) => {
  const [email, changeEmail] = useState('');
  const [password, changePassword] = useState('');
  const classes = useStyles();
  console.log("login mount")
  const handleEmailChange = useCallback(event => {
    changeEmail(event.target.value);
  }, []);

  const handlePasswordChange = useCallback(event => {
    changePassword(event.target.value);
  }, []);

  const handleLogin = useCallback(
    event => {
      event.preventDefault();

      loginThunk({ email, password });
    },
    [loginThunk, email, password]
  );
  const isLoggedIn = Boolean(getTokenThunk());

  if (isLoggedIn) {
    return <Redirect to={RoutesMap.Home} />;
  }


  return (
    <>
      {loading && <LinearProgress className={classes.progressBar} />}

      <Grid container component="main" className={classes.root}>
        <CssBaseline />
        <Grid item sm={4} md={7} className={classes.image} />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <div className={classes.paper}>
            <Avatar className={classes.avatar}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign in
            </Typography>
            {error && <Alert severity="error">Password incorrect or no email is found!</Alert>}

            <form className={classes.form} noValidate onSubmit={handleLogin}>
              <TextField
                onChange={handleEmailChange}
                value={email}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
              />
              <TextField
                onChange={handlePasswordChange}
                value={password}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={loading}
              >
                Sign In
              </Button>
              <Grid container>
                <Grid item xs>
                  <Link href="#" variant="body2">
                    Forgot password?
                  </Link>
                </Grid>
                <Grid item>
                  <Link href={RoutesMap.Register} variant="body2">
                    Don't have an account? Sign Up
                  </Link>
                </Grid>
              </Grid>
              <Box mt={5}>
                <Copyright />
              </Box>
            </form>
          </div>
        </Grid>
      </Grid>
    </>
  );
};
*/
export default Login;
